/**
 * Track definitions.
 *
 * A track is just data: a closed loop of control points plus the props
 * (boost / jump pads) scattered along it. The {@link Track} class turns this
 * into geometry at runtime. This separation is deliberate — the future level
 * editor will produce and consume exactly this shape, and multiplayer will
 * sync it by id.
 */

export type PadKind = "boost" | "jump";

export interface PadSpec {
  kind: PadKind;
  /** 0..1 position along the track loop. */
  t: number;
  /** -1..1 lateral offset across the road (0 = centre line). */
  offset: number;
  /**
   * For `jump` pads: how hard it launches you (1 = a small hop). Crank it up to
   * turn a pad into a ramp that can clear a gap for a risk shortcut.
   */
  power?: number;
  /**
   * Heading the pad points, in degrees relative to the track direction (0 =
   * straight down the track, + = angled right, - = left). Lets a jump fling you
   * off-axis across a gap, or a boost kick you into a corner. Omit for straight.
   */
  angle?: number;
}

/** A control point. X/Z position, **Y elevation** (the road follows Y, so
 * raising/lowering Y between points gives hills, dips, drops and crests),
 * optional **bank** angle in degrees (roll about the direction of travel, so a
 * section tilts into a turn — positive banks the right edge up), and an optional
 * **half-width** override in world units (the road widens/narrows toward it;
 * omit to use the track's base width), and an optional **wrap**: how far round,
 * in degrees, the road's cross-section curls (0 = flat, 180 = a half-pipe, 360
 * = a closed pipe you drive round the inside of; negative curls it down, so
 * -360 is a cylinder you ride the outside of). The half-width is then the arc's
 * half-length, so a pipe of radius r is 360 with a half-width of pi*r. The
 * editor edits the first five. */
export type ControlPoint = [x: number, y: number, z: number, bank?: number, halfWidth?: number, wrap?: number];

/** The scenery set a course is dressed in (sky, ground, props, road colours).
 * Editor tracks have none and get the neon grid. */
export type ThemeId = "neon" | "ocean" | "port" | "city" | "casino" | "desert" | "storm" | "sky" | "space";

export interface TrackSpec {
  id: string;
  name: string;
  /** Japanese subtitle shown under the name in the menus. */
  jp?: string;
  theme?: ThemeId;
  /** Road half-width in world units. */
  roadHalfWidth: number;
  /** Control points of the centre-line loop, in XZ with Y as elevation. */
  points: ControlPoint[];
  /**
   * Optional road "up" (surface normal) per control point, parallel to
   * `points`. Where one is given the road follows it instead of the bank, so it
   * can bank past vertical, loop, corkscrew or run upside down; the craft stays
   * on the surface whichever way is up. Omitted (or null) = the usual frame:
   * level across, tilted by `bank`.
   */
  ups?: (readonly [number, number, number] | null)[];
  pads: PadSpec[];
}

export const TRACKS: TrackSpec[] = [
  {
    id: "neon-circuit",
    name: "Neon Circuit",
    jp: "ネオン・サーキット",
    theme: "neon",
    // Wide road (Wipeout/F-Zero scale) so high speed reads well and turns
    // aren't claustrophobic.
    roadHalfWidth: 52,
    // A big, fast stadium loop built for speed:
    //  - a very long start straight (x=0, +Z) to wind it out, with a CHASM
    //    shortcut partway: the road bulges right around a ~150u gap and the ramp
    //    at the lip lets you fly straight across (risk: come up short and you
    //    fall in). The straight CONTINUES well past the rejoin, so overshooting
    //    the jump is safe — only undershooting punishes you.
    //  - sweeping, large-radius turns at each end (no brutal hairpins).
    //  - a HILL (Y up to 10) on the top sweeper and a VALLEY (Y −4) on the long
    //    back straight for verticality.
    // 2x scale of the original layout (footprint + elevation all doubled).
    points: [
      [0, 0, -900], // 0  start / finish, heading +Z (long main straight)
      [0, 0, -360], // 1
      [0, 10, 160], // 2  road rises into the launch lip
      [0, 14, 400], // 3  RAMP lip — chasm ahead (x=0, z 400..700)
      [280, 6, 500], // 4  main road bulges right around the chasm...
      [400, 0, 600], // 5  bulge apex (far right)
      [240, 6, 680], // 6
      [0, 14, 700], // 7  ...rejoins the +Z line past the ~300u gap (landing zone)
      [0, 6, 960], // 8  main straight continues (overshoot lands safely here)
      [0, 0, 1280], // 9  top of the main straight
      [180, 6, 1480], // 10 sweeping right turn begins, climbing
      [480, 16, 1600], // 11 hill climb
      [820, 20, 1560], // 12 hill crest (highest point)
      [1040, 14, 1340], // 13 descend
      [1120, 6, 1040], // 14 now heading -Z onto the back straight
      [1120, 0, 720], // 15 long back straight
      [1120, -8, 280], // 16 valley dip (lowest point)
      [1120, 0, -200], // 17
      [1100, 0, -560], // 18
      [1000, 4, -840], // 19 sweeping left turn back toward start
      [740, 8, -1040], // 20
      [400, 4, -1120], // 21
      [120, 0, -1080], // 22
      [0, 0, -1000], // 23 straighten onto x=0 before the start line
    ],
    // NOTE: pad `t` values are calibrated to the actual arc of this spline
    // (Catmull-Rom samples aren't evenly spaced), verified against the
    // centre-line dump.
    pads: [
      { kind: "boost", t: 0.03, offset: 0 }, // wind up the main straight
      // Shortcut ramp dead-centre at the lip (t≈0.083) — clear the ~150u chasm
      // to skip the bulge and rejoin at t≈0.25. Overshoot is safe (the straight
      // continues); undershoot drops you in.
      { kind: "jump", t: 0.083, offset: 0, power: 1.5 },
      { kind: "boost", t: 0.17, offset: 0 }, // reward for the safe bulge route
      { kind: "boost", t: 0.4, offset: 0 }, // up the hill sweeper
      { kind: "boost", t: 0.52, offset: 0 }, // over the crest
      { kind: "jump", t: 0.61, offset: 0, power: 0.7 }, // hop on the back straight
      { kind: "boost", t: 0.78, offset: 0 }, // out of the bottom sweeper
      { kind: "boost", t: 0.92, offset: 0 }, // onto the start straight
    ],
  },
  // ---- The eight courses below follow the track-design guideline: each has a
  // named signature, real height (a 210-520u span), crossings or spirals with
  // 57u+ of clearance, and full-width ramps on the dives. They're drawn in
  // tools/courses/designs.ts and checked headless: every ship laps them with
  // bots (on the centre line and out in the lanes) and with pads, and every
  // jump lands for every ship from a slow, full-speed or boosted approach.
  {
    // Figure-8 over the sea: start under the bridge, climb onto it and sweep round ~340 up, cross back over the start, then the shore S-bends down to the water and the lagoon leap.
    id: "sapphire-sea",
    name: "Sapphire Sea",
    jp: "サファイア・シー",
    theme: "ocean",
    roadHalfWidth: 52,
    points: [
      [0, 9.5, -212], [0, 12, 0], [0, 14, 177], [0, 16.1, 354],
      [0, 18.1, 531], [0, 48.5, 707], [0, 78.8, 884], [0, 109.2, 1061, -7.6],
      [37, 136.7, 1245, -14.5], [141, 164.2, 1401, -14.7], [296, 191.7, 1505, -14.9], [480, 219.2, 1541, -8],
      [630, 255.9, 1541], [780, 292.7, 1541, -8], [964, 305.2, 1505, -14.9], [1119, 317.7, 1401, -14.7],
      [1223, 330.2, 1245, -14.5], [1260, 342.7, 1061, -4.5], [1271, 342.7, 957, 15.1], [1305, 342.7, 858],
      [1339, 342.7, 759, -15.1], [1350, 342.7, 656, -14.7], [1313, 327.7, 472, -14.5], [1209, 312.7, 316, -14.7],
      [1054, 297.7, 212, -14.9], [870, 282.7, 176, -7.3], [689, 252.1, 176], [507, 221.6, 176],
      [326, 191, 176], [144, 160.5, 176], [-37, 129.9, 176], [-218, 104.8, 176],
      [-400, 79.7, 176, 6.6], [-508, 72.2, 161, 16.1, 58], [-610, 64.7, 119, -2.2, 58], [-747, 59.7, 69, -16.8, 58],
      [-893, 54.7, 69, -16.8, 58], [-1030, 49.7, 119, -2.4, 58], [-1131, 44.7, 161, 16.4, 58], [-1240, 39.7, 176, 15.9, 58],
      [-1423, 34.7, 139, 14.5], [-1579, 29.7, 35, 14.7], [-1683, 24.7, -121, 14.5], [-1720, 19.7, -304, 9.1],
      [-1720, 16.3, -424, 9], [-1683, 14.8, -608, 14.5], [-1579, 13.3, -764, 14.7], [-1423, 11.8, -868, 14.7],
      [-1240, 10.3, -904, 8.9], [-1120, 11.7, -904], [-1045, 9.7, -884], [-990, 7.7, -829],
      [-935, 7.7, -775], [-860, 7.7, -754], [-785, 7.7, -775], [-730, 7.7, -829],
      [-675, 6.7, -884], [-600, 5.7, -904], [-480, 7.1, -904, 8.8], [-296, 7.1, -868, 14.9],
      [-141, 7.1, -764, 14.7], [-37, 7.1, -608, 14.5], [0, 7.1, -424, 7],
    ],
    pads: [
      { kind: "boost", t: 0.0476, offset: 0 },
      { kind: "boost", t: 0.1984, offset: 0 },
      { kind: "boost", t: 0.7619, offset: 0 },
      { kind: "jump", t: 0.7619, offset: 0, power: 2.1, angle: 5.7 },
    ],
  },
  {
    // Port town at dusk: up the crane spiral to the top deck, then THE DIVE: a full-width ramp off the edge, ~200 down, flying over the start straight; the docks, the container chicane, the dock-basin jump, and home through THE LONG PIPE, which takes the last corner inside it.
    id: "harbor-leap",
    name: "Harbor Leap",
    jp: "ハーバー・リープ",
    theme: "port",
    roadHalfWidth: 50,
    points: [
      [0, 10.6, -186], [0, 10, 0], [0, 9.4, 186], [0, 8.8, 371],
      [0, 18, 621, -8.9], [33, 35.5, 753, -25.1], [124, 53, 854, -25.4], [253, 70.5, 900, -25.2],
      [387, 88, 880, -25.3], [496, 105.5, 799, -24.9], [555, 123, 676, -25.6], [548, 140.5, 540, -25.1],
      [478, 158, 423, -25.3], [361, 175.5, 353, -25.4], [225, 193, 347, -24.9], [102, 210.5, 405, -25.3],
      [21, 228, 514, -25.3], [1, 245.5, 649, -25.3], [47, 263, 777, -25.3], [148, 280.5, 868, -25.1],
      [280, 298, 901, -8.4], [554, 338.2, 901, -8.2], [719, 339.8, 857, -21.5], [840, 341.5, 736, -21.5],
      [884, 343.2, 571, -21.4], [840, 344.8, 406, -21.5], [719, 346.5, 285, -21.5], [554, 348.2, 241, -9.8],
      [354, 347.5, 241], [254, 297.2, 241, 0, 60], [154, 246.9, 241, 0, 60], [54, 196.5, 241, 0, 60],
      [-46, 146.2, 241, 0, 60], [-221, 140.6, 241, 0, 60], [-396, 135.1, 241, 0, 60], [-571, 129.5, 241, 0, 60],
      [-746, 123.9, 241, 0, 60], [-896, 93.4, 241], [-1046, 63, 241, 10.8], [-1226, 53, 193, 19.7],
      [-1358, 43, 61, 19.7], [-1406, 33, -119, 10.8], [-1406, 24.8, -271, 12.8], [-1371, 24.8, -401],
      [-1336, 24.8, -531, -27.3], [-1371, 24.8, -661], [-1406, 24.8, -791, 11], [-1406, 24.2, -991],
      [-1386, 22.2, -1066], [-1331, 20.2, -1121], [-1276, 20.2, -1176], [-1256, 20.2, -1251],
      [-1276, 20.2, -1326], [-1331, 20.2, -1381], [-1386, 19.2, -1436], [-1406, 18.2, -1511],
      [-1406, 17.7, -1664, 10.8], [-1358, 17.7, -1844, 19.9], [-1226, 17.7, -1975, 19.4], [-1046, 17.7, -2024, 10],
      [-860, 17.1, -2024, 0, 78.5], [-710, 16.6, -2024, 0, 78.5, 360], [-535, 16, -2024, 0, 78.5, 360], [-360, 15.4, -2024, 0, 78.5, 360],
      [-180, 15.4, -1975, 0, 78.5, 360], [-48, 15.4, -1844, 0, 78.5, 360], [0, 15.4, -1664, 0, 78.5, 360], [0, 14.6, -1414, 0, 78.5, 360],
      [0, 14.1, -1264, 0, 78.5], [0, 13.6, -1114], [0, 13, -928], [0, 12.4, -742],
      [0, 11.8, -557], [0, 11.2, -371],
    ],
    pads: [
      { kind: "boost", t: 0.027, offset: 0 },
      { kind: "boost", t: 0.277, offset: 0 },
      { kind: "jump", t: 0.3649, offset: -0.8, power: 1 },
      { kind: "jump", t: 0.3649, offset: -0.4, power: 1 },
      { kind: "jump", t: 0.3649, offset: 0, power: 1 },
      { kind: "jump", t: 0.3649, offset: 0.4, power: 1 },
      { kind: "jump", t: 0.3649, offset: 0.8, power: 1 },
      { kind: "boost", t: 0.5541, offset: 0 },
      { kind: "jump", t: 0.6216, offset: 0, power: 2.1, angle: 3.7 },
    ],
  },
  {
    // Night city figure-8: climb into THE TWIST, hard-banked S-bends up among the towers, sweep back over the start and plunge down the canyon.
    id: "skyline-twist",
    name: "Skyline Twist",
    jp: "スカイライン・ツイスト",
    theme: "city",
    roadHalfWidth: 46,
    points: [
      [-40, 16.7, -150, 27], [0, 20, 0, 11.8], [0, 21.9, 199], [0, 23.9, 399],
      [0, 25.8, 598], [0, 27.8, 798], [0, 76.3, 997], [0, 124.7, 1196, -12.6],
      [35, 139.7, 1326, -30, 40], [130, 154.7, 1422, -30, 40], [225, 164.7, 1517, 34, 40], [260, 174.7, 1647, 34, 40],
      [295, 182.2, 1777, -36, 40], [390, 189.7, 1872, -36, 40], [520, 197.2, 1907, -36, 40], [650, 204.7, 1872, -36, 40],
      [780, 214.7, 1837, 34, 40], [910, 224.7, 1872, 34, 40], [1014, 233.9, 1932, -16.4, 40], [1144, 236.4, 1967, -36, 40],
      [1274, 238.9, 1932, -36, 40], [1369, 241.4, 1837, -36, 40], [1404, 243.9, 1707, -36, 40], [1439, 243.9, 1577, 34, 40],
      [1534, 243.9, 1482, 34, 40], [1636, 242.3, 1388, -25], [1689, 240.6, 1260, -25.2], [1683, 238.9, 1122, -25.2],
      [1619, 237.3, 999, -25], [1509, 235.6, 914, -25.3], [1374, 233.9, 884, -9.7], [1150, 221.2, 884],
      [927, 208.5, 884], [880, 206.8, 884], [834, 205.2, 884], [787, 203.5, 884],
      [740, 201.8, 884], [694, 200.2, 884], [647, 198.5, 884], [600, 196.8, 884],
      [554, 195.2, 884], [507, 193.5, 884], [460, 191.8, 884], [414, 190.2, 884],
      [367, 188.5, 884], [143, 175.8, 884], [-80, 163.1, 884], [-230, 134.5, 884],
      [-380, 106, 884], [-530, 62.5, 884], [-680, 18.9, 884, 13.7], [-830, 8.9, 844, 27],
      [-940, -1.1, 734, 27], [-980, -11.1, 584, 14.4], [-980, -23.2, 450, 15.7], [-945, -23.2, 320, 31.1],
      [-850, -23.2, 225], [-755, -23.2, 130, -31.1], [-720, -23.2, 0, -0.1], [-680, -16.5, -150, 27],
      [-570, -9.8, -260, 27], [-420, -3.2, -300, 15.1], [-300, 10, -300, 15.1], [-150, 13.3, -260, 27],
    ],
    ups: [
      null, null, null, null,
      null, null, null, null,
      null, null, null, null,
      null, null, null, null,
      null, null, null, null,
      null, null, null, null,
      null, null, null, null,
      null, null, null, null,
      null, [0, 0.866, 0.5], [0, 0.5, 0.866], [0, 0, 1],
      [0, -0.5, 0.866], [0, -0.866, 0.5], [0, -1, 0], [0, -0.866, -0.5],
      [0, -0.5, -0.866], [0, 0, -1], [0, 0.5, -0.866], [0, 0.866, -0.5],
      [0, 1, 0], null, null, null,
      null, null, null, null,
      null, null, null, null,
      null, null, null, null,
      null, null, null, null,
    ],
    pads: [
      { kind: "boost", t: 0.0313, offset: 0 },
      { kind: "boost", t: 0.3656, offset: 0 },
    ],
  },
  {
    // Casino figure-8: two banked bowls, the east one climbing ~250, crossing back over the start diagonal on the way home.
    id: "lucky-loop",
    name: "Lucky Loop",
    jp: "ラッキー・ループ",
    theme: "casino",
    roadHalfWidth: 46,
    points: [
      [-636, 0, -591, 30], [-500, 0, -500, 9], [-339, 0.6, -339], [-177, 1.2, -177],
      [-16, 1.8, -16], [105, 7.9, 105], [226, 14.1, 226], [347, 20.3, 347, -10.9],
      [476, 35.3, 433, -32], [630, 50.3, 464, -32], [783, 65.3, 433, -32], [912, 80.3, 347, -32],
      [997, 104.6, 262, -12.7], [1084, 129.6, 132, -32], [1114, 154.6, -21, -32], [1114, 172.9, -141],
      [1114, 186.6, -210, -2.4], [1113, 225.6, -268, -15.7], [1109, 284, -307, -38], [1103, 352.9, -321, -38],
      [1094, 421.8, -307, -38], [1082, 480.2, -268, -11], [1068, 519.2, -210, -1.2], [1052, 532.9, -141, -1.8],
      [1037, 519.2, -72, 3.1], [1023, 480.2, -14, 11], [1011, 421.8, 25, 38], [1002, 352.9, 39, 38],
      [995, 284, 25, 38], [992, 225.6, -14, 11], [991, 186.6, -72, 0.4], [990, 172.9, -141, 1.4],
      [990, 173.2, -261, -12.6], [960, 163.2, -414, -32], [873, 153.2, -544, -32], [743, 140.7, -631, -34],
      [590, 128.2, -661, -34], [437, 115.7, -631, -34], [308, 103.2, -544, -34], [173, 96.6, -409, 0.2],
      [38, 89.9, -275, -0.4], [-96, 83.3, -140, 0.4], [-231, 76.6, -6, -0.4], [-365, 70, 129, 0.2],
      [-500, 63.4, 264, 9.9], [-636, 55.9, 355, 34], [-797, 48.4, 387, 34], [-958, 40.9, 355, 34],
      [-1094, 33.4, 264, 34], [-1179, 25.7, 179, 12.4], [-1270, 20.7, 43, 34], [-1302, 15.7, -118, 34],
      [-1270, 10.7, -279, 34], [-1179, 5.7, -415, 34], [-1094, 0, -500, 12.4], [-958, 0, -591, 30],
      [-797, 0, -623, 30],
    ],
    ups: [
      null, null, null, null,
      null, null, null, null,
      null, null, null, null,
      null, null, null, null,
      [0, 0.924, 0.383], [0, 0.707, 0.707], [0, 0.383, 0.924], [0, 0, 1],
      [0, -0.383, 0.924], [0, -0.707, 0.707], [0, -0.924, 0.383], [0, -1, 0],
      [0, -0.924, -0.383], [0, -0.707, -0.707], [0, -0.383, -0.924], [0, 0, -1],
      [0, 0.383, -0.924], [0, 0.707, -0.707], [0, 0.924, -0.383], [0, 1, 0],
      null, null, null, null,
      null, null, null, null,
      null, null, null, null,
      null, null, null, null,
      null, null, null, null,
      null, null, null, null,
      null,
    ],
    pads: [
      { kind: "boost", t: 0.0526, offset: 0 },
      { kind: "boost", t: 0.2544, offset: 0 },
      { kind: "boost", t: 0.8509, offset: 0 },
    ],
  },
  {
    // Desert figure-8: THE DUNES roll down the home straight; round the plateau, down into the canyon under the dunes, THE CANYON LEAP and the climb out.
    id: "dune-rush",
    name: "Dune Rush",
    jp: "デューン・ラッシュ",
    theme: "desert",
    roadHalfWidth: 58,
    points: [
      [0, 31.3, -237, 5.9], [0, 40, 0], [0, 40.7, 198], [0, 71.2, 348],
      [0, 101.8, 498], [0, 72.3, 648], [0, 42.9, 798], [0, 73.4, 948],
      [0, 103.9, 1098], [0, 74.5, 1248], [0, 45, 1398, -7.2], [35, 47.5, 1574, -13.2],
      [135, 50, 1723, -13.2], [284, 52.5, 1822, -12.9], [460, 55, 1858, -7], [625, 61.1, 1858],
      [790, 67.2, 1858, -7], [966, 63.4, 1822, -12.9], [1115, 59.7, 1723, -13.2], [1215, 55.9, 1574, -13.2],
      [1250, 52.2, 1398, -6.6], [1250, 20.3, 1223], [1250, -11.6, 1048], [1250, -53.3, 866],
      [1250, -95, 685, -6.8], [1220, -97.5, 531, -15.4], [1133, -100, 402, -14.9], [1003, -102.5, 315, -15.2],
      [850, -105, 285, -6.2], [640, -104.2, 284, -0.1], [430, -103.5, 284], [220, -102.7, 284],
      [70, -102.2, 284, 0, 62], [0, -104.2, 266, 0, 62], [-51, -106.2, 214, 0, 62], [-103, -106.2, 163, 0, 62],
      [-173, -106.2, 144, 0, 62], [-243, -106.2, 163, 0, 62], [-294, -106.2, 214, 0, 62], [-345, -107.2, 266, 0, 62],
      [-415, -108.2, 284, 0, 62], [-640, -107.4, 284, 7.3], [-830, -100.7, 234, 16.2], [-969, -94, 94, 15.8],
      [-1020, -87.4, -96, 8.3], [-1020, -62.5, -277, 8.3], [-969, -52.5, -467, 16], [-830, -42.5, -606, 16],
      [-640, -32.5, -657, 7.6], [-420, 1.3, -657, 6.1], [-259, 8.8, -625, 14.4], [-123, 16.3, -534, 14.4],
      [-32, 23.8, -398, 14.4],
    ],
    pads: [
      { kind: "boost", t: 0.0189, offset: 0 },
      { kind: "boost", t: 0.2453, offset: 0 },
      { kind: "jump", t: 0.5849, offset: 0, power: 2.1, angle: 3.8 },
    ],
  },
  {
    // Storm: THE HALF-PIPE down the start straight, THE SPIRE, 2½ banked turns climbing ~450 round the lightning spire, then the lightning drop off a full-width ramp and the long U-turn descent.
    id: "thunder-spire",
    name: "Thunder Spire",
    jp: "サンダー・スパイア",
    theme: "storm",
    roadHalfWidth: 46,
    points: [
      [-1650, 25, -340], [-1500, 10, -340], [-1300, 10, -340], [-1100, 10, -340],
      [-900, 10, -340], [-750, 10, -340], [-600, 10, -340, 0, 46, 180], [-375, 10, -340, 0, 46, 180],
      [-150, 10, -340, 0, 46, 180], [0, 10, -340, 11.1], [165, 24.5, -297, 20.6], [289, 39, -180, 21],
      [340, 53.5, -17, 20.9], [305, 68.1, 150, 20.7], [194, 82.6, 279, 20.8], [34, 97.1, 338, 20.8],
      [-134, 111.6, 312, 20.7], [-269, 126.1, 208, 21], [-336, 140.6, 51, 20.7], [-319, 155.2, -118, 21],
      [-221, 169.7, -258, 20.7], [-68, 184.2, -333, 20.9], [102, 198.7, -324, 20.8], [246, 213.2, -234, 20.7],
      [329, 227.7, -85, 20.9], [329, 242.3, 85, 20.9], [246, 256.8, 234, 20.7], [102, 271.3, 324, 20.8],
      [-68, 285.8, 333, 20.9], [-221, 300.3, 258, 20.7], [-319, 314.8, 118, 21], [-336, 329.4, -51, 20.7],
      [-269, 343.9, -208, 21], [-134, 358.4, -312, 20.7], [34, 372.9, -338, 20.8], [194, 387.4, -279, 20.8],
      [305, 401.9, -150, 20.7], [340, 416.5, 17, 20.9], [289, 431, 180, 21], [165, 445.5, 297, 20.6],
      [0, 460, 340, 11.1], [-150, 455, 340], [-300, 450, 340], [-500, 450, 340],
      [-600, 400, 340, 0, 56], [-700, 350, 340, 0, 56], [-800, 300, 340, 0, 56], [-900, 250, 340, 0, 56],
      [-1075, 240, 340, 0, 56], [-1250, 230, 340, 0, 56], [-1425, 220, 340, 0, 56], [-1600, 210, 340, 0, 56],
      [-1800, 190, 340, 9.8], [-1970, 165, 294, 20.8], [-2094, 140, 170, 20.8], [-2140, 115, 0, 21],
      [-2094, 90, -170, 20.8], [-1970, 65, -294, 20.8], [-1800, 40, -340, 11.3],
    ],
    pads: [
      { kind: "boost", t: 0.0508, offset: 0 },
      { kind: "boost", t: 0.7034, offset: 0 },
      { kind: "jump", t: 0.7119, offset: -0.8, power: 1 },
      { kind: "jump", t: 0.7119, offset: -0.4, power: 1 },
      { kind: "jump", t: 0.7119, offset: 0, power: 1 },
      { kind: "jump", t: 0.7119, offset: 0.4, power: 1 },
      { kind: "jump", t: 0.7119, offset: 0.8, power: 1 },
    ],
  },
  {
    // Above the clouds: start 600 up, drop terrace to terrace off two ramps, THE DRAGON SLOPE sweeping down ~500 to THE SPINDLE (the road wrapped all the way round a cylinder), and the updraft sweepers climbing back.
    id: "cloud-terrace",
    name: "Cloud Terrace",
    jp: "クラウド・テラス",
    theme: "sky",
    roadHalfWidth: 50,
    points: [
      [0, 571.2, -152], [0, 600, 0], [0, 603.5, 152], [0, 609.3, 402],
      [0, 576, 477, 0, 58], [0, 542.7, 552, 0, 58], [0, 542.9, 777, 0, 58], [0, 543.1, 1002, 0, 58],
      [0, 509.8, 1077, 0, 58], [0, 476.6, 1152, 0, 58], [0, 476.8, 1377, 0, 58], [0, 476.9, 1602, -7.1, 58],
      [32, 471.9, 1762, -16.6], [123, 466.9, 1899, -16.8], [259, 461.9, 1990, -16.8], [420, 456.9, 2022, -7.9],
      [604, 461.2, 2022], [788, 465.4, 2022], [973, 469.7, 2022], [1157, 473.9, 2022, -7.6],
      [1333, 458.9, 1987, -15.4], [1482, 443.9, 1887, -15.2], [1582, 428.9, 1738, -15.4], [1617, 413.9, 1562, -7.2],
      [1617, 363.6, 1362], [1617, 313.2, 1162, 6.3], [1636, 283.2, 1035, 26], [1693, 253.2, 921, 26],
      [1760, 226.5, 765, -30], [1760, 199.8, 595, -30], [1693, 173.2, 439, -30], [1636, 148.2, 324, 26],
      [1617, 123.2, 198, 26], [1617, 121, 98, 0, 94], [1617, 117.7, -52, 0, 94, -360], [1617, 113.2, -264, 0, 94, -360],
      [1617, 108.6, -475, 0, 94, -360], [1617, 104.1, -687, 0, 94, -360], [1617, 99.5, -898, 0, 94, -360], [1617, 96.3, -1048, 0, 94],
      [1617, 94.1, -1148, -9.8], [1582, 114.1, -1324, -30], [1482, 134.1, -1473, -30], [1333, 154.1, -1573, -30],
      [1157, 174.1, -1608, -30], [925, 225.8, -1608], [694, 277.4, -1608, -6.7], [518, 302.4, -1573, -30],
      [369, 327.4, -1473, -30], [269, 352.4, -1324, -30], [234, 377.4, -1148, -30], [204, 407.4, -977, 24],
      [117, 437.4, -827, 24], [30, 467.4, -676, -24], [0, 497.4, -505, -24], [0, 534.3, -328],
    ],
    pads: [
      { kind: "boost", t: 0.0179, offset: 0 },
      { kind: "jump", t: 0.0357, offset: -0.8, power: 0.9 },
      { kind: "jump", t: 0.0357, offset: -0.4, power: 0.9 },
      { kind: "jump", t: 0.0357, offset: 0, power: 0.9 },
      { kind: "jump", t: 0.0357, offset: 0.4, power: 0.9 },
      { kind: "jump", t: 0.0357, offset: 0.8, power: 0.9 },
      { kind: "jump", t: 0.1071, offset: -0.8, power: 0.9 },
      { kind: "jump", t: 0.1071, offset: -0.4, power: 0.9 },
      { kind: "jump", t: 0.1071, offset: 0, power: 0.9 },
      { kind: "jump", t: 0.1071, offset: 0.4, power: 0.9 },
      { kind: "jump", t: 0.1071, offset: 0.8, power: 0.9 },
      { kind: "boost", t: 0.3214, offset: 0 },
    ],
  },
  {
    // Deep space: swells and the first void jump, up to the top, THE METEOR STREAM (hard-banked arcs diving ~340 into the void), the second void jump and the long climb home.
    id: "star-stream",
    name: "Star Stream",
    jp: "スター・ストリーム",
    theme: "space",
    roadHalfWidth: 48,
    points: [
      [0, -8.8, -273, -7.2], [0, 0, 0], [0, 1.5, 205], [0, 37.6, 355],
      [0, 73.6, 505], [0, 39.7, 655], [0, 5.8, 805], [0, 46.9, 955],
      [0, 88, 1105], [20, 86, 1180], [75, 84, 1235], [130, 84, 1290],
      [150, 84, 1365], [130, 84, 1440], [75, 84, 1495], [20, 83, 1549],
      [0, 82, 1624], [0, 68.1, 1774], [0, 54.2, 1924, -9.5], [34, 64.2, 2097, -18],
      [132, 74.2, 2243, -17.9], [278, 84.2, 2340, -17.9], [450, 94.2, 2374, -8.2], [655, 157, 2374, -7.9],
      [798, 123.7, 2349, -50], [925, 90.4, 2276, -50], [1018, 57, 2164, -50], [1126, 27, 2041, 80],
      [1273, -3, 1969, 80], [1437, -33, 1958, 80], [1592, -63, 2011, 80], [1758, -87, 2065, -105],
      [1932, -111, 2046, -105], [2083, -135, 1959, -105], [2186, -159, 1818, -105], [2222, -183, 1647, -105],
      [2222, -194.7, 1449], [2222, -206.5, 1252], [2242, -208.5, 1177], [2297, -210.5, 1122],
      [2352, -210.5, 1067], [2372, -210.5, 992], [2352, -210.5, 917], [2297, -210.5, 862],
      [2242, -211.5, 807], [2222, -212.5, 732], [2222, -204.5, 535], [2222, -196.4, 337],
      [2222, -188.4, 140], [2222, -180.4, -58, -8.7], [2190, -172.9, -219, -19.2], [2099, -165.4, -355, -19.1],
      [1963, -157.9, -446, -19.2], [1802, -150.4, -478, -8.7], [1604, -129.2, -478], [1407, -108, -478, 7.9],
      [1249, -98, -506, 28], [1111, -88, -586, 28], [973, -78, -665, -28], [815, -68, -693, -28],
      [618, -53.4, -693], [420, -38.8, -693, -8.7], [259, -31.3, -661, -19.2], [123, -23.8, -570, -19.1],
      [32, -16.3, -434, -19.2],
    ],
    pads: [
      { kind: "boost", t: 0.0154, offset: 0 },
      { kind: "jump", t: 0.1077, offset: 0, power: 2.1, angle: -4.9 },
      { kind: "boost", t: 0.3231, offset: 0 },
      { kind: "jump", t: 0.5538, offset: 0, power: 2.1, angle: 3.3 },
    ],
  },
];

export function getTrackById(id: string): TrackSpec {
  return TRACKS.find((t) => t.id === id) ?? TRACKS[0];
}
